import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

interface ConfidenceBarProps {
  signal: number;
  conflictDetected: boolean;
}

/**
 * Maps a claim signal (range approximately [-1, 1]) to a bar fill percentage.
 * Uses discrete bands so the bar reads as a human-friendly strength indicator
 * rather than a precise number. The absolute value is used so contradicting
 * claims (negative signal) show their strength equally.
 */
function signalToPercent(signal: number): number {
  const abs = Math.abs(signal);
  if (abs >= 0.8) return 100;
  if (abs >= 0.6) return 75;
  if (abs >= 0.4) return 50;
  if (abs >= 0.2) return 30;
  return 10;
}

const ConfidenceBarRoot = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const ThickProgressBar = styled(LinearProgress)({
  height: 6,
  borderRadius: 3,
});

const ConflictLabel = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  display: "block",
}));

export function ConfidenceBar({ signal, conflictDetected }: ConfidenceBarProps) {
  const { t } = useTranslation();
  const value = signalToPercent(signal);

  return (
    <ConfidenceBarRoot>
      <ThickProgressBar
        variant="determinate"
        value={value}
        color={conflictDetected ? "warning" : "primary"}
      />
      {conflictDetected && (
        <ConflictLabel variant="caption" color="warning.main">
          {t("confidenceBar.conflictingEvidence")}
        </ConflictLabel>
      )}
    </ConfidenceBarRoot>
  );
}
